import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import streamApi from '../../services/streamApi';
import './MediaGallery.css';

function MediaGallery({ userId, isOwnProfile, isEditMode }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxItem, setLightboxItem] = useState(null);

  useEffect(() => {
    if (!isOwnProfile && !userId) {
      setLoading(false);
      return;
    }

    async function fetchMedia() {
      try {
        let items;
        if (isOwnProfile) {
          await streamApi.autoConnect();
          const allMedia = await streamApi.getMediaLibrary();
          items = (Array.isArray(allMedia) ? allMedia : []).filter(
            (m) => m.approval_status === 'approved',
          );
        } else {
          items = await streamApi.getPublicUserMedia(userId);
          if (!Array.isArray(items)) items = [];
        }
        setMedia(items);
      } catch (err) {
        console.error('Failed to load media gallery:', err);
        setMedia([]);
      } finally {
        setLoading(false);
      }
    }

    fetchMedia();
  }, [userId, isOwnProfile]);

  if (loading) return null;
  if (media.length === 0 && !isEditMode) return null;

  return (
    <div className="media-gallery">
      {media.length === 0 ? (
        <div className="media-gallery__empty">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <p>No approved media yet. Upload images from the Upload page to see them here.</p>
        </div>
      ) : (
        <div className="media-gallery__grid">
          {media.map((item, index) => {
            const imageUrl = streamApi.resolveImageUrl(item.image_url);
            const isGif = (item.image_url || '').toLowerCase().endsWith('.gif');
            return (
              <button
                key={item.id}
                type="button"
                className="media-gallery__item"
                style={{ animationDelay: `${index * 0.08}s` }}
                onClick={() => setLightboxItem(item)}
              >
                <img src={imageUrl} alt={item.title || 'Media'} className="media-gallery__item-img" {...(!isGif && { loading: 'lazy' })} />
                <div className="media-gallery__item-overlay">
                  <span className="media-gallery__item-title">{item.title || ''}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {lightboxItem && (
        <div className="media-gallery__lightbox" onClick={() => setLightboxItem(null)} role="dialog" aria-modal="true">
          <div className="media-gallery__lightbox-container" onClick={(e) => e.stopPropagation()} style={{ position: 'relative', display: 'inline-block' }}>
            <img src={streamApi.resolveImageUrl(lightboxItem.image_url)} alt={lightboxItem.title || 'Media'} className="media-gallery__lightbox-img" />
            <button type="button" className="media-gallery__lightbox-close" onClick={() => setLightboxItem(null)} aria-label="Close lightbox" style={{ position: 'absolute', top: '-12px', right: '-12px' }}>&times;</button>
          </div>
        </div>
      )}
    </div>
  );
}

MediaGallery.propTypes = {
  userId: PropTypes.string,
  isOwnProfile: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool,
};

MediaGallery.defaultProps = {
  userId: null,
  isEditMode: false,
};

export default MediaGallery;
